import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Grid,
  GridItem,
  Separator,
  Link as StyledLink,
  Typography,
  theme,
} from '@fe-toolkit/web-components/ssr'

import {
  ContractDetailsCard,
  ContractType,
  DetailsListSeparator,
  getBadgeForOrder,
  getBadgeForPlan,
} from '@giffgaff/broadband-common'

import { customEvent } from '@fe-toolkit/eventing'

import { useFeatureEnabled } from '@fe-toolkit/utility/client/tracking-context'

import dayjs from 'dayjs'

import { BannersWithActions } from '@/components/organisms/Banners'

import { convertMbpsToGbps } from '@/utils/convertMbpsToGbps'
import { formatPriceFromPenceToPounds } from '@/utils/formatPrice'

import { formatCannotChangeInfo, getNotificationCopy } from '@/utils/formatPlanAmendments'

import { useEffectOnceWithCondition } from '@/hooks/useEffectOnceWithCondition'

import { ECommerceOrderScheduleEntry, PlanBuyoutDetails } from '@/api'

import { useUIFlags } from '@/context'

import { basePath } from '@/constants'

import AdaptiveAlert from '@/components/molecules/AdaptiveAlert'
import { ExitFeesOfferCard } from '@/components/molecules/ExitFeesOfferCard'
import { LegalDisclaimerBanner } from '@/components/molecules/LegalDisclaimerBanner'
import { ReferralOfferCard } from '@/components/molecules/ReferralOfferCard'

import ChangePlanNotification from '../ChangePlan/components/ChangePlanNotification'

import { PaymentsSection } from './components/PaymentsSection'
import { ProfilePlanCard } from './components/ProfilePlanCard'
import { ProfileSection } from './components/ProfileSection'

import { NoPlanOrOrderSection } from './components/NoPlanOrOrderSection/NoPlanOrOrderSection'

import { MemberGreeting } from './components/MemberGreeting/MemberGreeting'

import { PseudoTabNavigation } from './components/PseudoTabNavigation/PseudoTabNavigation'

import { CeasedServiceSection } from './components/CeasedServiceSection/CeasedServiceSection'

import { DATE_FORMAT } from '../PausePlan/consts'

import type { ProfileMemberData } from './Profile.types'

import type { OrchestratedPayment, PaymentCard, Plan, PlanAmendmentDetails, ProfileOverview } from '@/api/api.types'

export type TProfileProps = {
  profileOverview?: ProfileOverview
  handleDownloadContract: (contractDocId: string, type: ContractType) => void
  memberData: ProfileMemberData | undefined
  amendments?: {
    details?: PlanAmendmentDetails
    newPlan?: Plan
  }
  ecommerceOrderSchedule?: ECommerceOrderScheduleEntry | undefined
  cardDetails?: PaymentCard | undefined
  orchestratedPayments?: OrchestratedPayment[] | undefined
  buyoutDetails?: PlanBuyoutDetails
  onUploadExitBillClick?: () => void
  isCreatingEvidenceSession?: boolean
}

const Profile = ({
  profileOverview,
  ecommerceOrderSchedule,
  handleDownloadContract,
  amendments,
  cardDetails,
  orchestratedPayments,
  memberData,
  buyoutDetails,
  onUploadExitBillClick,
  isCreatingEvidenceSession,
}: TProfileProps) => {
  const { order, plan, hasActivePlan, hasCeasedService, referralCode } = profileOverview ?? {}
  const memberId = memberData?.id ? `${memberData?.id}` : undefined
  const { enabled: isBuyoutFeatureEnabled } = useFeatureEnabled('broadband-inlife-contract-buyout')

  const showBuyoutClaimSubmittedAlert =
    buyoutDetails?.evidenceSessionId &&
    buyoutDetails?.showAlert === true &&
    buyoutDetails?.reason === 'plan.buyout.claim_submitted'
  const showBuyoutClaimApprovedAlert =
    buyoutDetails?.evidenceSessionId &&
    buyoutDetails?.showAlert === true &&
    buyoutDetails?.reason === 'plan.buyout.claim_approved'

  // only ever have contracts for ecom products
  const hasContract: boolean = !!plan?.ecommerce

  useEffectOnceWithCondition(
    ([_, plan]) => {
      customEvent('bb_plan_impression', {
        planStatus: plan?.active === true ? 'active' : 'inactive',
        planId: plan?.id ?? '',
      })
    },
    ([hasActivePlan, plan]) => {
      return !!(hasActivePlan && plan)
    },
    [hasActivePlan, plan] as const,
  )

  let profileType = 'NO_PLAN_OR_ORDER'

  if (profileOverview && (plan || order)) {
    profileType = 'PLAN_OR_ORDER'
  } else if (hasCeasedService) {
    profileType = 'CEASED_SERVICE'
  }

  return (
    <>
      {memberId && <BannersWithActions memberId={memberId} order={order} plan={plan} />}

      <Container
        as="section"
        backgroundColor="wildSand"
        py="large"
        sx={{
          minHeight: '70vh',
        }}
      >
        <MemberGreeting memberData={memberData} />

        <Box marginBottom="large" marginTop="medium">
          <PseudoTabNavigation
            onClick={() => {
              customEvent('gg_click', { contextType: 'bb_dashboard_mobile_plan_click' })
            }}
          />
        </Box>

        {showBuyoutClaimSubmittedAlert && (
          <AdaptiveAlert isMinor borderRadius="small" marginBottom="large" variant="info">
            <Typography as="p" variant="speakUp">
              We're checking your exit fee documents before we can add your credit.
            </Typography>
            <Typography as="p" variant="speak">
              Sit tight, we'll be in touch when we have news.
            </Typography>
            <Button isSmall href={`${basePath}claim/${buyoutDetails.evidenceSessionId}`} testId="track-claim-button">
              Track claim
            </Button>
          </AdaptiveAlert>
        )}

        {showBuyoutClaimApprovedAlert && (
          <AdaptiveAlert isMinor borderRadius="small" marginBottom="large" variant="success">
            <Typography as="p" variant="speakUp">
              Exit fee credit approved
            </Typography>
            <Typography as="p" variant="speak">
              Awesome news. We'll credit your account after your second monthly payment.
            </Typography>
            <Button isSmall href={`${basePath}claim/${buyoutDetails.evidenceSessionId}`} testId="review-claim-button">
              Review claim
            </Button>
          </AdaptiveAlert>
        )}

        {profileType === 'PLAN_OR_ORDER' && (
          <>
            <Grid
              columnGap="medium"
              contentFlow="row dense"
              gridColumns={3}
              gridRows="auto"
              marginBottom="large"
              rowGap="large"
            >
              {hasActivePlan && plan ? (
                // if user has active plan
                <GridItem medium={1} tiny={3}>
                  <PlanSection amendments={amendments} plan={plan} />
                </GridItem>
              ) : (
                // if user has plan which is not yet activated
                <GridItem medium={1} tiny={3}>
                  <OrderSection order={order} plan={plan} />
                </GridItem>
              )}
              <GridItem medium={3} tiny={3}>
                <OffersSection
                  buyoutDetails={buyoutDetails}
                  isCreatingEvidenceSession={isCreatingEvidenceSession}
                  referralCode={referralCode}
                  onUploadExitBillClick={onUploadExitBillClick}
                />
              </GridItem>
              <GridItem medium={1} tiny={3}>
                <PaymentsSection
                  cardDetails={cardDetails}
                  ecommerceOrderSchedule={ecommerceOrderSchedule}
                  orchestratedPayments={orchestratedPayments}
                  order={order}
                  plan={plan}
                />
              </GridItem>
              {hasContract && plan && (
                <GridItem medium={1} tiny={3}>
                  <ContractSection handleDownloadContract={handleDownloadContract} plan={plan} />
                </GridItem>
              )}
            </Grid>
          </>
        )}

        {profileType === 'NO_PLAN_OR_ORDER' && (
          <>
            <NoPlanOrOrderSection />
          </>
        )}

        {profileType === 'CEASED_SERVICE' && <CeasedServiceSection />}
      </Container>
      {isBuyoutFeatureEnabled && <LegalDisclaimerBanner />}
    </>
  )
}

function PlanSection({
  plan,
  amendments,
}: Readonly<{
  plan: Plan
  amendments: TProfileProps['amendments']
}>) {
  const {
    UIFlags: { PAUSE_PLAN_FEATURE_ENABLED },
  } = useUIFlags()
  const hasPendingAmendments = amendments?.details?.canAmend === false
  const cannotChangeInfo = formatCannotChangeInfo(amendments?.newPlan, amendments?.details)
  const cannotChangeNotificationCopy = getNotificationCopy(amendments?.details?.reason)
  const isPromoPlan = !!plan.promotionalProduct
  const badge = getBadgeForPlan(plan.active)
  const activePause =
    plan.speedRestriction?.state === 'ACTIVE' && plan.speedRestriction?.reason?.type === 'MEMBER_PAUSE'
      ? plan.speedRestriction
      : null
  const pendingPause =
    plan.speedRestriction?.state === 'PENDING' && plan.speedRestriction?.reason?.type === 'MEMBER_PAUSE'
      ? plan.speedRestriction
      : null

  return (
    <ProfileSection>
      <Typography as="h2" color="black" marginBottom={theme.spacing.small} variant="loudBold">
        My plan
      </Typography>

      <ProfilePlanCard
        badgeContent={
          badge ? (
            <Box as="span" data-testid="plan-card-badge">
              <Badge backgroundColor={badge.backgroundColor} color={badge.color} variant="murmurBold">
                {badge.text}
              </Badge>
            </Box>
          ) : undefined
        }
        data-ecommerce-order-schedule-id={plan.ecommerceOrderScheduleId}
        data-plan-id={plan.id}
        data-testid="plan"
        displayName={plan.displayName}
        price={
          <>
            £{plan.monthlyPrice}
            <Typography as="span" variant="speakUp">
              /mo
            </Typography>
          </>
        }
        priceNote={
          isPromoPlan ? (
            <>
              <Typography as="span" sx={{ display: 'block' }} variant="whisper">
                Then{' '}
                <Typography as="span" variant="whisperLoud">
                  {formatPriceFromPenceToPounds(Number(plan.postPromotionMonthlyPrice) * 100)}/mo
                </Typography>{' '}
              </Typography>
              from {dayjs(plan.promotionEndDate).format('D MMM')}
            </>
          ) : undefined
        }
        speed={convertMbpsToGbps(plan.downstreamSpeedInMbps)}
      >
        {hasPendingAmendments && (
          <ChangePlanNotification>
            <Typography as="p" data={cannotChangeInfo} variant="speakUp">
              {cannotChangeNotificationCopy.title}
            </Typography>
            <Typography as="p" data={cannotChangeInfo} variant="speak">
              {cannotChangeNotificationCopy.copy}
            </Typography>
          </ChangePlanNotification>
        )}

        {activePause && (
          <AdaptiveAlert isMinor borderRadius="small" variant="info">
            <Typography as="p" variant="speakUp">
              Your plan is currently paused
            </Typography>
            <Typography as="p" variant="speak">
              Your full speed will resume at {dayjs(activePause.endDateTime).format(DATE_FORMAT)}
            </Typography>
            <Button isSmall href={`/profile/broadband/pause-plan/${plan.id}`}>
              Unpause my plan
            </Button>
          </AdaptiveAlert>
        )}

        {pendingPause && (
          <AdaptiveAlert isMinor borderRadius="small" variant="info">
            <Typography as="p" variant="speakUp">
              Upcoming plan pause
            </Typography>
            <Typography as="p" variant="speak">
              Your plan will be paused from {dayjs(pendingPause.startDateTime).format(DATE_FORMAT)} and will resume at{' '}
              {dayjs(pendingPause.endDateTime).format(DATE_FORMAT)}
            </Typography>
            <Button isSmall href={`/profile/broadband/pause-plan/${plan.id}`}>
              Cancel pause request
            </Button>
          </AdaptiveAlert>
        )}

        {plan.active && (
          <>
            <DetailsListSeparator colour="alto" />
            <StyledLink
              chevron
              fullWidth
              href={`/profile/broadband/change-plan/${plan.id}`}
              onClick={() => {
                customEvent('bb_change_plan_clicked', {
                  planId: plan.id,
                  planStatus: plan.active === true ? 'active' : 'inactive',
                })
              }}
            >
              Change plan
            </StyledLink>
          </>
        )}
        {PAUSE_PLAN_FEATURE_ENABLED && (
          <>
            <DetailsListSeparator colour="alto" />
            <StyledLink
              chevron
              fullWidth
              href={`/profile/broadband/pause-plan/${plan.id}`}
              onClick={() => {
                customEvent('bb_pause_plan_clicked', {
                  planId: plan.id,
                  planStatus: plan.active === true ? 'active' : 'inactive',
                })
              }}
            >
              Pause plan
            </StyledLink>
          </>
        )}
      </ProfilePlanCard>
    </ProfileSection>
  )
}

function OrderSection({ plan, order }: Readonly<{ plan: ProfileOverview['plan']; order: ProfileOverview['order'] }>) {
  const orderManagement = getOrderManagementOption({ order, plan })
  const badge = getBadgeForOrder(order?.state)

  return (
    <ProfileSection>
      <Typography as="h2" color="black" marginBottom={theme.spacing.small} variant="loudBold">
        My order
      </Typography>
      {plan && order && (
        <ProfilePlanCard
          badgeContent={
            badge ? (
              <Box as="span" data-testid="plan-card-badge">
                <Badge backgroundColor={badge.backgroundColor} color={badge.color} variant="murmurBold">
                  {badge.text}
                </Badge>
              </Box>
            ) : undefined
          }
          displayName={plan.displayName}
          price={`£${plan.monthlyPrice}`}
          priceFrequency="a month"
          speed={convertMbpsToGbps(plan.downstreamSpeedInMbps)}
        >
          <Separator colour="gallery" marginBottom={theme.spacing.small} marginTop={theme.spacing.small} />
          {orderManagement && (
            <StyledLink chevron fullWidth display="flex" href={orderManagement.href}>
              {orderManagement.title}
            </StyledLink>
          )}
        </ProfilePlanCard>
      )}
    </ProfileSection>
  )
}

function ContractSection({
  plan,
  handleDownloadContract,
}: Readonly<{
  plan: NonNullable<ProfileOverview['plan']>
  handleDownloadContract: TProfileProps['handleDownloadContract']
}>) {
  return (
    <ProfileSection>
      <Typography as="h2" color="black" marginBottom={theme.spacing.small} variant="loudBold">
        My contract
      </Typography>
      <ContractDetailsCard
        showCardAction
        cancelServiceClassName="intercom-workflow-broadband-cancel"
        cardDetailsClassName="intercom-workflow-broadband-accessible-format-request"
        plan={plan}
        showServiceMode={false}
        sx={{ flexGrow: 1 }}
        onDownloadContractClick={handleDownloadContract}
      />
    </ProfileSection>
  )
}

function OffersSection({
  buyoutDetails,
  isCreatingEvidenceSession,
  onUploadExitBillClick,
  referralCode,
}: Readonly<{
  buyoutDetails?: PlanBuyoutDetails
  isCreatingEvidenceSession?: boolean
  onUploadExitBillClick?: () => void
  referralCode?: string
}>) {
  const {
    UIFlags: { REFERRALS_ENABLED },
  } = useUIFlags()
  const { enabled: buyoutBannerEnabled } = useFeatureEnabled('broadband-inlife-contract-buyout')

  const canShowBuyoutBanner = buyoutDetails?.canBeBoughtOut === true && buyoutDetails?.showAlert === true

  const showOffers = (buyoutBannerEnabled && canShowBuyoutBanner) || (REFERRALS_ENABLED && !!referralCode)

  if (!showOffers) {
    return null
  }

  return (
    <ProfileSection>
      <Typography as="h2" color="black" marginBottom={theme.spacing.small} variant="loudBold">
        Offers for you
      </Typography>

      <Flex
        sx={{
          gap: theme.spacing.small,
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        {buyoutBannerEnabled && canShowBuyoutBanner && (
          <ExitFeesOfferCard
            isCreatingEvidenceSession={isCreatingEvidenceSession}
            onUploadExitBillClick={onUploadExitBillClick}
          />
        )}
        {REFERRALS_ENABLED && referralCode && <ReferralOfferCard referralCode={referralCode} />}
      </Flex>
    </ProfileSection>
  )
}

export default Profile

function getOrderManagementOption({ order, plan }: Pick<ProfileOverview, 'order' | 'plan'>) {
  if (order?.id && !order.needsToPaySetup) {
    return { title: 'View order', href: `broadband/manage-order/${order.id}` } // Relative path from profile route for Next.js (e.g. /profile/broadband/manage-order/12345)
  }

  if (plan?.id && order?.needsToPaySetup) {
    return { title: 'Complete order', href: `/broadband/order-confirmation?planId=${plan.id}` } // Absolute path from root for Next.js (eg . /broadband/order-confirmation?planId=12345)
  }

  return null
}
